import { Modal, Skeleton } from "@adafia/ui";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useBlogPost } from "../../hooks/useBlogPost";
import { CommentSection } from "./CommentSection";
import { PostContent } from "./PostContent";
import { PostHeader } from "./PostHeader";

export const BlogPost = () => {
	const { id } = useParams<{ id: string }>();
	const [commentToDelete, setCommentToDelete] = useState<string | null>(null);
	const {
		post,
		blocks,
		interactions,
		comments,
		isLoading,
		likeMutation,
		blockLikeMutation,
		commentMutation,
		deleteCommentMutation,
	} = useBlogPost(id);

	if (isLoading && !post) {
		return (
			<article className="blog-post">
				<div style={{ marginBottom: "3rem" }}>
					<Skeleton width="120px" height="1.5rem" />
				</div>
				<header className="blog-post-header">
					<div className="blog-meta">
						<Skeleton width="150px" height="1rem" />
						<Skeleton width="100px" height="2rem" borderRadius="20px" />
					</div>
					<Skeleton
						width="80%"
						height="3rem"
						style={{ margin: "1rem 0 2rem 0" }}
					/>
				</header>
				<div className="blog-content">
					<Skeleton
						width="100%"
						height="2rem"
						style={{ marginBottom: "1rem" }}
					/>
					<Skeleton
						width="95%"
						height="2rem"
						style={{ marginBottom: "1rem" }}
					/>
					<Skeleton
						width="100%"
						height="2rem"
						style={{ marginBottom: "1rem" }}
					/>
					<Skeleton
						width="40%"
						height="2rem"
						style={{ marginBottom: "3rem" }}
					/>

					<Skeleton width="100%" height="20rem" />
				</div>
			</article>
		);
	}

	if (!post) {
		return <p>Post not found.</p>;
	}

	return (
		<article className="blog-post">
			<div
				style={{
					display: "flex",
					justifyContent: "flex-start",
					alignItems: "center",
					marginBottom: "3rem",
				}}
			>
				<Link
					to="/"
					style={{
						display: "flex",
						alignItems: "center",
						gap: "0.5rem",
						textDecoration: "none",
						color: "var(--color-text-muted)",
						fontSize: "0.875rem",
					}}
				>
					<ArrowLeft size={16} /> Back to Journal
				</Link>
			</div>

			<PostHeader
				post={post}
				interactions={interactions}
				onLike={() => likeMutation.mutate()}
				isLiking={likeMutation.isPending}
			/>

			<PostContent
				post={post}
				blocks={blocks}
				interactions={interactions}
				comments={comments}
				onBlockLike={(bid) => blockLikeMutation.mutate(bid)}
				onCommentSubmit={(data) => commentMutation.mutate(data)}
				onCommentDelete={setCommentToDelete}
				isCommentPending={commentMutation.isPending}
			/>

			<CommentSection
				comments={comments}
				onCommentSubmit={(data) => commentMutation.mutate(data)}
				onCommentDelete={setCommentToDelete}
				isCommentPending={commentMutation.isPending}
			/>

			<Modal
				isOpen={!!commentToDelete}
				onClose={() => setCommentToDelete(null)}
				title="Delete Comment"
			>
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: "1.5rem",
						padding: "0.5rem 0",
					}}
				>
					<p style={{ color: "var(--color-text-muted)", fontSize: "0.95rem" }}>
						Are you sure you want to delete this comment? This action cannot be
						undone.
					</p>
					<div
						style={{
							display: "flex",
							justifyContent: "flex-end",
							gap: "0.75rem",
							marginTop: "0.5rem",
						}}
					>
						<button
							type="button"
							onClick={() => setCommentToDelete(null)}
							style={{
								padding: "8px 16px",
								background: "transparent",
								border: "1px solid var(--color-border)",
								borderRadius: "6px",
								color: "var(--color-text-muted)",
								cursor: "pointer",
								fontSize: "0.875rem",
								fontWeight: 500,
							}}
							onMouseEnter={(e) => {
								e.currentTarget.style.background = "var(--color-bg-secondary)";
							}}
							onMouseLeave={(e) => {
								e.currentTarget.style.background = "transparent";
							}}
						>
							Cancel
						</button>
						<button
							type="button"
							onClick={() => {
								if (commentToDelete) {
									deleteCommentMutation.mutate(commentToDelete);
									setCommentToDelete(null);
								}
							}}
							style={{
								padding: "8px 16px",
								background: "var(--color-error, #ef4444)",
								border: "1px solid var(--color-error, #ef4444)",
								borderRadius: "6px",
								color: "#fff",
								cursor: "pointer",
								fontSize: "0.875rem",
								fontWeight: 500,
							}}
							onMouseEnter={(e) => {
								e.currentTarget.style.background =
									"var(--color-error-hover, #dc2626)";
								e.currentTarget.style.borderColor =
									"var(--color-error-hover, #dc2626)";
							}}
							onMouseLeave={(e) => {
								e.currentTarget.style.background =
									"var(--color-error, #ef4444)";
								e.currentTarget.style.borderColor =
									"var(--color-error, #ef4444)";
							}}
						>
							Delete
						</button>
					</div>
				</div>
			</Modal>
		</article>
	);
};
